import {useMemo} from 'react';
import {CourseRating} from '@udemy-v2/browse-course';
import {useCourseRatingRefetchQuery} from '@/generated/graphql';
import {useClpCourse} from '../context/clp-course-provider.react-component';
import {useFeatures} from '@udemy-v2/react-experimentation';
import {COURSE_VARIATION_FEATURE_CODE} from '../utils/feature';

/**
 * EXPERIMENT: Client-side rating refetch for cv_ui_aggregated_reviews.
 * TODO: Remove this hook and revert consumers to useClpCourse().rating when experiment concludes.
 */

interface UseCourseRatingResult {
    rating: CourseRating | null | undefined;
    isLoading: boolean;
}

export function useCourseRating(): UseCourseRatingResult {
    const course = useClpCourse();
    const {isLoading: isLoadingFeatures} = useFeatures([COURSE_VARIATION_FEATURE_CODE]);

    const hasVariations = !!course.variationAggregation?.variations.length;
    // Refetch should happen regardless of the feature flag
    // to avoid using the cached rating
    const shouldRefetchRatings = hasVariations;

    const {data, isLoading: isLoadingQuery} = useCourseRatingRefetchQuery(
        {landingURL: course.urlCourseLanding},
        {
            enabled: shouldRefetchRatings,
            staleTime: 5 * 60 * 1000,
        },
    );

    // Determine if we should show loading state:
    // For courses WITH variations:
    //   - If feature flags haven't loaded yet: show loading to prevent rating flash
    //   - If feature flags loaded AND refetch enabled: show loading while query is loading OR until we have received a response
    //   - If feature flags loaded AND refetch disabled: show original rating
    // For courses WITHOUT variations: always show original rating immediately
    const shouldShowLoading =
        hasVariations && (isLoadingFeatures || (shouldRefetchRatings && (isLoadingQuery || !data)));

    const rating = useMemo(() => {
        // If we should be refetching but don't have data yet, don't return any rating
        if (shouldShowLoading) {
            return null;
        }
        // Return fetched rating if we have it
        if (shouldRefetchRatings && data?.courseByLandingURL?.rating) {
            return data.courseByLandingURL.rating;
        }
        // Otherwise return the original rating
        return course.rating;
    }, [shouldShowLoading, shouldRefetchRatings, data?.courseByLandingURL?.rating, course.rating]);

    const isRatingLoading = shouldShowLoading;

    return {
        rating,
        isLoading: isRatingLoading,
    };
}
